import { AxisCode } from '../classification/classification.enums.js'
import { WorkfrontTargetCardinality, WorkfrontTargetLevel, WorkfrontTargetType } from './workfront-intake.enums.js'

/** One path of the canonical contract that a field mapping may write. */
export interface IWorkfrontTarget {
	/** e.g. `child.assetTypeCode`, `parent.tuple.brand`, `task.stage`. */
	path: string
	level: WorkfrontTargetLevel
	label: string
	type: WorkfrontTargetType
	/** The axis an `axis` target takes values of. */
	axis?: string
	/** The code-owned enum an `enum` target takes members of. */
	enumName?: string
	cardinality: WorkfrontTargetCardinality
	/** A record with no value for it is held. */
	required: boolean
	/**
	 * Filled by the mapper from the bundle's structure, never by a field mapping: it
	 * is listed so admins see it, and save-time validation does not ask for a source.
	 */
	structural: boolean
	/** Where the apply step writes it. */
	landsIn: string
}

const { PARENT, CHILD, TASK } = WorkfrontTargetLevel
const { ONE, MANY } = WorkfrontTargetCardinality
const T = WorkfrontTargetType

type TargetSpec = Omit<IWorkfrontTarget, 'level' | 'required' | 'structural' | 'cardinality'> & {
	cardinality?: WorkfrontTargetCardinality
	required?: boolean
	structural?: boolean
}

const at =
	(level: WorkfrontTargetLevel) =>
	(spec: TargetSpec): IWorkfrontTarget => ({
		cardinality: ONE,
		required: false,
		structural: false,
		...spec,
		level,
	})

const parent = at(PARENT)
const child = at(CHILD)
const task = at(TASK)

const axisTarget = (level: 'parent' | 'child', axis: AxisCode, label: string, cardinality = ONE): TargetSpec => ({
	path: `${level}.tuple.${axis}`,
	label,
	type: T.AXIS,
	axis,
	cardinality,
	landsIn: `classification.${axis}`,
})

/** The targets both levels have, with the level's own path prefix. */
const shared = (level: 'parent' | 'child'): TargetSpec[] => [
	{ path: `${level}.title`, label: 'Title', type: T.STRING, required: true, landsIn: 'work_order.title' },
	{ path: `${level}.dueAt`, label: 'Due date', type: T.DATETIME, landsIn: 'work_order.due_at' },
	{ path: `${level}.description`, label: 'Description', type: T.SECTIONS, landsIn: 'work_order.external_description' },
	// The parent's brand and geography are required: they decide who works on the brief.
	{ ...axisTarget(level, AxisCode.BRAND, 'Brand › product', MANY), required: level === 'parent' },
	{
		...axisTarget(level, AxisCode.GEOGRAPHY, 'Region › country', level === 'parent' ? MANY : ONE),
		required: level === 'parent',
	},
	axisTarget(level, AxisCode.BUSINESS_UNIT, 'Business unit'),
	axisTarget(level, AxisCode.AUDIENCE, 'Audience', MANY),
	axisTarget(level, AxisCode.PRODUCT_FAMILY, 'Product family', MANY),
	{
		path: `${level}.relatedLinks`,
		label: 'Related links',
		type: T.LIST,
		cardinality: MANY,
		landsIn: 'work_order_link',
	},
]

/**
 * Every path a field mapping may write. ATLAS owns this list: a target is something the
 * apply step must know how to write, so adding one is a code change of both sides.
 */
export const WORKFRONT_TARGETS: readonly IWorkfrontTarget[] = [
	...shared('parent').map(parent),
	parent({ path: 'parent.owner', label: 'Merck owner', type: T.STRING, landsIn: 'work_order.external_owner_name' }),
	parent({ path: 'parent.budget', label: 'Budget', type: T.MONEY, landsIn: 'work_order.budget_amount_minor' }),
	parent({
		path: 'parent.plannedAssetCount',
		label: 'Planned assets',
		type: T.INTEGER,
		landsIn: 'work_order.planned_asset_count',
	}),
	parent({ path: 'parent.pmRegion', label: 'PM region', type: T.STRING, landsIn: 'wf_bundle_canonical' }),
	parent({
		path: 'parent.pmUserId',
		label: 'PM',
		type: T.STRING,
		structural: true,
		landsIn: 'work_order.pm_user_id',
	}),
	parent({
		path: 'parent.readiness.briefComplete',
		label: 'Brief complete',
		type: T.BOOLEAN,
		landsIn: 'wf_bundle_canonical',
	}),
	parent({
		path: 'parent.readiness.costComplete',
		label: 'Cost complete',
		type: T.BOOLEAN,
		landsIn: 'wf_bundle_canonical',
	}),
	...shared('child').map(child),
	child(axisTarget('child', AxisCode.SPECIES, 'Species › subspecies')),
	child(axisTarget('child', AxisCode.CHANNEL, 'Channel › sub-channel')),
	child(axisTarget('child', AxisCode.LOCALE, 'Language')),
	child({
		path: 'child.parentRef',
		label: 'Parent',
		type: T.STRING,
		required: true,
		structural: true,
		landsIn: 'work_order.parent_work_order_id',
	}),
	child({
		path: 'child.assetTypeCode',
		label: 'Type of asset',
		type: T.ENUM,
		enumName: 'AssetTypeCode',
		landsIn: 'attribute.asset_type_code',
	}),
	child({
		path: 'child.complexityLevel',
		label: 'Size',
		type: T.ENUM,
		enumName: 'ComplexityLevel',
		landsIn: 'attribute.complexity_level',
	}),
	child({ path: 'child.campaign', label: 'Campaign', type: T.STRING, landsIn: 'attribute.campaign' }),
	child({
		path: 'child.plannedCost',
		label: 'Planned cost',
		type: T.MONEY,
		landsIn: 'work_order.planned_cost_amount_minor',
	}),
	child({ path: 'child.finalCost', label: 'Final cost', type: T.MONEY, landsIn: 'work_order.final_cost_amount_minor' }),
	child({
		path: 'child.externalCostStatus',
		label: 'Cost status',
		type: T.STRING,
		landsIn: 'work_order.external_cost_status',
	}),
	child({
		path: 'child.expectedPublishingDate',
		label: 'Expected publishing date',
		type: T.DATE,
		landsIn: 'work_order.expected_publishing_date',
	}),
	child({
		path: 'child.isAdaptation',
		label: 'Is an adaptation',
		type: T.BOOLEAN,
		landsIn: 'work_order.is_adaptation',
	}),
	child({
		path: 'child.adaptationNeeds',
		label: 'Adaptation needs',
		type: T.HTML,
		landsIn: 'work_order.adaptation_needs',
	}),
	child({
		path: 'child.originalCodes',
		label: 'Original codes',
		type: T.LIST,
		cardinality: MANY,
		landsIn: 'work_order_original_code',
	}),
	child({
		path: 'child.referencedDocuments',
		label: 'Referenced documents',
		type: T.LIST,
		cardinality: MANY,
		landsIn: 'work_order_referenced_document',
	}),
	child({ path: 'child.targetVeevaCode', label: 'Veeva code', type: T.STRING, landsIn: 'attribute.target_veeva_code' }),
	child({ path: 'child.scopeChanged', label: 'Scope change', type: T.BOOLEAN, landsIn: 'work_order.scope_changed' }),
	child({
		path: 'child.scopeChangeReason',
		label: 'Scope change reason',
		type: T.STRING,
		landsIn: 'work_order.scope_change_reason',
	}),
	task({ path: 'task.stage', label: 'Stage', type: T.STRING, landsIn: 'work_order_external_task.stage' }),
	task({
		path: 'task.responsible',
		label: 'Responsible',
		type: T.STRING,
		landsIn: 'work_order_external_task.responsible',
	}),
	task({ path: 'task.gate', label: 'Gate', type: T.STRING, landsIn: 'work_order_external_task.gate' }),
	task({
		path: 'task.instruction',
		label: 'Instruction',
		type: T.STRING,
		landsIn: 'work_order_external_task.instruction',
	}),
]

/** The catalogue entry of a path, or undefined when the path is not a target. */
export const findWorkfrontTarget = (path: string): IWorkfrontTarget | undefined =>
	WORKFRONT_TARGETS.find((target) => target.path === path)
